import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Building2,
  Users, 
  Settings, 
  CheckCircle, 
  ArrowRight,
  Database,
  Shield,
  Zap,
  Target,
  BarChart3,
  Globe,
  Clock,
  MessageSquare,
  GraduationCap,
  Cloud,
  Mail,
  Calendar,
  FileText,
  Video,
  Smartphone,
  Monitor,
  Workflow,
  Bot,
  Briefcase,
  UserCheck,
  TrendingUp
} from 'lucide-react';

const MicrosoftSolutions: React.FC = () => {
  const dynamics365Modules = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Dynamics 365 Sales",
      description: "Comprehensive CRM solution to optimize your sales processes with pipeline management and customer interaction tracking.",
      features: ["Sales pipeline management", "Customer interaction tracking", "Customizable automation", "Business rule enforcement"]
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Dynamics 365 Marketing",
      description: "Marketing automation platform to nurture leads and guide prospects through the buyer's journey.",
      features: ["Lead nurturing", "Marketing automation", "Buyer journey mapping", "Built-in analytics"]
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Dynamics 365 Field Service",
      description: "Deliver efficient onsite service with intelligent scheduling and workflow automation.",
      features: ["Onsite service delivery", "Workflow automation", "Intelligent scheduling", "Mobile workforce"]
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Dynamics 365 Customer Service",
      description: "Provide exceptional customer service with case management and knowledge base integration.",
      features: ["Case management", "Knowledge base", "Multi-channel support", "Service analytics"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Dynamics 365 Finance",
      description: "Comprehensive financial management with real-time insights and global compliance.",
      features: ["Financial reporting", "Budget management", "Global compliance", "Cash flow forecasting"]
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Dynamics 365 Supply Chain",
      description: "Optimize operations with intelligent supply chain management and predictive analytics.",
      features: ["Inventory management", "Demand forecasting", "Warehouse optimization", "Quality control"]
    }
  ];

  const microsoft365Apps = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Outlook & Exchange",
      description: "Professional email, calendar, and contact management with advanced security features.",
      features: ["Enterprise email", "Calendar management", "Contact organization", "Advanced security"]
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Office Applications",
      description: "Word, Excel, PowerPoint, and more with real-time collaboration and cloud storage.",
      features: ["Real-time collaboration", "Cloud storage", "Version control", "Mobile access"]
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Microsoft Teams",
      description: "Unified communication platform for chat, video calls, file sharing, and collaboration.",
      features: ["Video conferencing", "Team chat", "File sharing", "App integrations"]
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "SharePoint Online",
      description: "Document management and collaboration platform with workflow automation.",
      features: ["Document libraries", "Workflow automation", "Team sites", "Content management"]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security & Compliance",
      description: "Advanced security features including threat protection and compliance management.",
      features: ["Threat protection", "Data loss prevention", "Compliance center", "Identity management"]
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Power BI",
      description: "Business intelligence and analytics platform for data visualization and insights.",
      features: ["Interactive dashboards", "Data modeling", "Real-time analytics", "Mobile reports"]
    }
  ];

  const benefits = [
    "Seamless integration between all Microsoft solutions",
    "Enterprise-grade security and compliance",
    "Scalable solutions that grow with your business",
    "24/7 Microsoft support and updates",
    "AI-powered insights and automation",
    "Mobile-first approach for modern workforce"
  ];

  const implementationProcess = [
    {
      step: "01",
      title: "Assessment & Planning",
      description: "Evaluate your current systems and plan the optimal Microsoft solution architecture.",
      color: "bg-blue-500"
    },
    {
      step: "02",
      title: "Solution Design",
      description: "Design customized Microsoft 365 and Dynamics 365 configurations for your needs.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "Data Migration",
      description: "Safely migrate your existing data to Microsoft cloud platforms with zero data loss.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "System Integration",
      description: "Integrate Microsoft solutions with your existing business applications and workflows.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Testing & Training",
      description: "Comprehensive testing and user training to ensure smooth adoption and usage.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Go-Live & Support",
      description: "Launch your Microsoft solutions with ongoing support and optimization services.",
      color: "bg-indigo-500"
    }
  ];

  const integrationFeatures = [
    "Single sign-on across all Microsoft applications",
    "Unified data model between Dynamics 365 and Office 365",
    "Power Platform integration for custom workflows",
    "Teams integration for collaboration within business apps",
    "Shared calendar and contact management",
    "Cross-platform mobile access",
    "Centralized admin and security management",
    "Common compliance and governance policies"
  ];

  return (
    <>
      <SEOHead
        title="Microsoft Solutions | CRMONCE - Dynamics 365 & Microsoft 365 Services"
        description="Comprehensive Microsoft Dynamics 365 and Microsoft 365 implementation services. Expert consulting, integration, and support for complete Microsoft business solutions."
        keywords="Microsoft Dynamics 365, Microsoft 365, Office 365, CRM implementation, ERP solutions, Microsoft consulting, business applications, cloud solutions, digital transformation"
        canonical="https://crmonce.com/microsoft-365"
        ogImage="/images/microsoft-solutions-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Microsoft Solutions - Dynamics 365 & Microsoft 365",
          "description": "Professional Microsoft Dynamics 365 and Microsoft 365 implementation and consulting services. Complete business transformation with integrated Microsoft solutions.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "Microsoft Solutions Consulting",
          "category": "Business Software Implementation",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Microsoft Solutions Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Dynamics 365 Implementation",
                  "description": "Complete Dynamics 365 CRM and ERP implementation services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Microsoft 365 Setup",
                  "description": "Microsoft 365 deployment and configuration services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Integration Services",
                  "description": "Seamless integration between Microsoft solutions and existing systems"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center mb-6">
                <Building2 className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Microsoft Solutions</h1>
              </div>
              <p className="text-xl text-blue-100 mb-8">
                Transform your business with comprehensive Microsoft Dynamics 365 and Microsoft 365 solutions. 
                Integrated platforms that empower your organization with intelligent business applications and modern workplace tools.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-all duration-300"
              >
                Start Your Transformation
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Dynamics 365 Solutions */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Microsoft Dynamics 365 Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive business applications that connect your entire organization with 
                intelligent CRM, ERP, and business process solutions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dynamics365Modules.map((module, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-blue-600 mb-4">{module.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {module.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {module.description}
                  </p>
                  <div className="space-y-2">
                    {module.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Microsoft 365 Solutions */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Microsoft 365 Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Modern workplace solutions that enable collaboration, productivity, and security 
                across your organization with cloud-first applications.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {microsoft365Apps.map((app, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="text-green-600 mb-4">{app.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {app.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {app.description}
                  </p>
                  <div className="space-y-2">
                    {app.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Benefits */}
        <section className="py-20">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Integrated Microsoft Ecosystem
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Experience the power of fully integrated Microsoft solutions working together 
                to streamline your business processes and enhance productivity.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {integrationFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-gray-700 font-medium">{feature}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Implementation Process */}
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Implementation Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We follow a proven methodology to ensure successful deployment and adoption 
                of Microsoft solutions in your organization.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {implementationProcess.map((process, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${process.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {process.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Microsoft Solutions?
              </h2>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Transform your business with the world's most trusted and comprehensive 
                business platform solutions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our Microsoft experts help you implement comprehensive solutions that 
                drive productivity, efficiency, and growth across your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  View Implementation Guide
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MicrosoftSolutions;
